import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './RatingDots.module.css';

// ADR 0061: a 1–5 rating as five numbered dots — the focus on a line of the ideal candidate profile. A radio
// group underneath (one tab stop, arrows pick, Home and End jump), so it works from the keyboard as a native one does.

const MIN_RATING = 1;
const MAX_RATING = 5;
const STEPS: Readonly<Record<string, 1 | -1>> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 };

interface RatingDotsProps {
  /** What is rated, for the group's name: "Focus for Plans delivery routes". */
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
  /** What each number means, keyed "1"…"5": read out as "5 of 5: Must have" and shown as the tooltip. */
  readonly hints?: Readonly<Record<string, string>>;
  readonly isDisabled?: boolean;
  readonly size?: 'md' | 'sm';
}

export function RatingDots({ label, value, onChange, hints = {}, isDisabled = false, size = 'md' }: RatingDotsProps): ReactNode {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function pick(next: number): void {
    if (next === value) return;
    onChange(next);
    refs.current[next - MIN_RATING]?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, dot: number): void {
    const step = STEPS[event.key];
    if (step !== undefined) {
      event.preventDefault();
      pick(Math.min(MAX_RATING, Math.max(MIN_RATING, dot + step)));
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      pick(event.key === 'Home' ? MIN_RATING : MAX_RATING);
    }
  }

  return (
    <span className={styles.dots} data-size={size} role="radiogroup" aria-label={label} aria-disabled={isDisabled || undefined}>
      {Array.from({ length: MAX_RATING }, (_, index) => {
        const dot = index + MIN_RATING;
        const hint = hints[String(dot)];
        return (
          <button
            key={dot}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={value === dot}
            aria-label={hint ? `${dot} of ${MAX_RATING}: ${hint}` : `${dot} of ${MAX_RATING}`}
            title={hint}
            tabIndex={(value >= MIN_RATING && value <= MAX_RATING ? value : MIN_RATING) === dot ? 0 : -1}
            className={styles.dot}
            disabled={isDisabled}
            onClick={() => pick(dot)}
            onKeyDown={(event) => onKeyDown(event, dot)}
          >
            {dot}
          </button>
        );
      })}
    </span>
  );
}
